import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ModeToggle } from "@/components/mode-toggle";
import Logo from "@/components/logo";

const InvitePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, setUser, axiosInstance } = useAuth();
  const { toast } = useToast();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    const fetchInvite = async () => {
      try {
        const response = await axiosInstance.get(`/invites/${token}`);
        setInvite(response.data);
      } catch (error) {
        console.error('Error fetching invite:', error);
        toast({
          description: "Invalid or expired invite link",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchInvite();
  }, [token, axiosInstance]);

  const handleAcceptInvite = async () => {
    setAccepting(true);
    try {
      const response = await axiosInstance.post(`/invites/${token}/accept`);
      
      setUser(response.data.user);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('activeOrganization', JSON.stringify(response.data.organization));

      toast({
        description: `You've joined ${response.data.organization.name}!`
      });
      
      navigate(`/onboarding`);
    } catch (error) {
      console.error('Error accepting invite:', error);
      toast({
        description: "Failed to accept invitation",
        variant: "destructive"
      });
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background relative">
        <div className="absolute top-8 left-8">
          <Logo />
        </div>
        <div className="absolute top-8 right-8">
          <ModeToggle />
        </div>
        <div className="flex items-center justify-center h-screen">
          Loading...
        </div>
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="min-h-screen bg-background relative">
        <div className="absolute top-8 left-8">
          <Logo />
        </div>
        <div className="absolute top-8 right-8">
          <ModeToggle />
        </div>
        <div className="container max-w-lg mx-auto pt-20">
          <Card>
            <CardHeader>
              <CardTitle>Invalid Invite</CardTitle>
              <CardDescription>This invite link is invalid or has expired.</CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative">
      <div className="absolute top-8 left-8">
        <Logo />
      </div>
      <div className="absolute top-8 right-8">
        <ModeToggle />
      </div>
      <div className="container max-w-lg mx-auto pt-20">
        <Card>
          <CardHeader>
            <CardTitle>Join {invite.organization.name}</CardTitle>
            <CardDescription>
              {invite.inviter.name} has invited you to join their organization
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button 
              className="w-full" 
              onClick={handleAcceptInvite}
              disabled={accepting}
            >
              {accepting ? "Joining..." : "Accept Invitation"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InvitePage; 